// /app — the one address to give people for the iPhone app.
//
// The App Store URL is long and unmemorable, and a new app ranks poorly in the
// store's own search for anything but the exact one-word name. So people are told
// "go to mysocialcal.com/app" instead, and this page does the rest:
//
// - On an iPhone or iPad browser it forwards straight to the App Store listing.
// - Anywhere else it shows the download button and the way into the web app.
// - Inside the app itself there is nothing to download, so it goes home.
//
// PUBLIC (see PUBLIC_PATHS in main.ts): the people this is for mostly have no
// account yet. Deliberately NOT a bare path, like /privacy: a signed-in reader
// gets the ordinary chrome, a signed-out one renders bare via chrome.ts.
//
// SocialTie ships inside the MySocialCal app, so the page names MySocialCal on
// both names rather than following brandName().

import { el } from '../dom.js';
import { navigate, type View } from '../router.js';
import { isNative } from '../platform.js';
import { authBrand } from './authBrand.js';

/** App Store ID 6809963032, live since 2026-10-02. See docs/plan-ios-ship.md. */
export const APP_STORE_URL = 'https://apps.apple.com/us/app/mysocialcal/id6809963032';

/**
 * Is this an iPhone, iPod or iPad browser?
 *
 * iPadOS Safari reports a desktop Mac user agent by default, so an iPad is only
 * distinguishable from a Mac by having a touch screen. No shipping Mac has one.
 */
export function isAppleMobile(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export const getAppView: View = (outlet) => {
  if (isNative()) {
    navigate('/', true);
    return;
  }
  if (isAppleMobile()) {
    // replace(), not assign(): Back from the App Store must not land here and
    // bounce straight back out again.
    location.replace(APP_STORE_URL);
  }

  outlet.append(
    el('main', { class: 'auth-card get-app' }, [
      authBrand('div'),
      el('h1', {}, 'Get MySocialCal on your iPhone'),
      el(
        'p',
        {},
        'Free on the App Store, for iPhone and iPad. Get a reminder on the day ' +
          'of every event and confirm with one tap.',
      ),
      el('p', {}, [el('a', { class: 'btn', href: APP_STORE_URL }, 'Download on the App Store')]),
      el('p', { class: 'muted' }, [
        'Or open the App Store and search for ',
        el('strong', {}, 'MySocialCal'),
        ', all one word.',
      ]),
      el('p', {}, [
        'No iPhone? It works in any browser: ',
        el('a', { href: '/login' }, 'Log in'),
        ' or ',
        el('a', { href: '/register' }, 'create an account'),
        '.',
      ]),
    ]),
  );
};
